import { useFormContext } from 'react-hook-form';

import type { FormMultiSelectProps } from '../types';
import type { FieldValues } from 'react-hook-form';

import { MultiSelect } from '@/components/MultiSelect';
import { Field, FieldDescription, FieldError } from '@/components/ui/field';
import { FormField } from '@/components/ui/form';

export const FormMultiSelect = <TFieldValues extends FieldValues = FieldValues>(
  props: FormMultiSelectProps<TFieldValues>,
) => {
  const {
    name,
    label,
    description,
    isRequired,
    options,
    multiSelectProps,
    control,
    onChange,
  } = props;
  const formContext = useFormContext<TFieldValues>();
  const formControl = control || formContext.control;
  const { clearErrors } = formContext;

  const fieldId = `form-field-${String(name)}`;

  return (
    <FormField
      control={formControl}
      name={name}
      render={({ field, fieldState }) => {
        const value = (field.value as string[]) || [];
        const hasError = !!fieldState.error;
        const fieldLabel = (
          <>
            {label}
            {isRequired && (
              <span
                className={
                  hasError ? 'text-destructive' : 'text-muted-foreground'
                }
              >
                *
              </span>
            )}
          </>
        );

        return (
          <Field data-invalid={hasError}>
            <MultiSelect
              {...multiSelectProps}
              id={fieldId}
              value={value}
              onValueChange={(selectedValues) => {
                field.onChange(selectedValues);
                clearErrors(name);
                onChange?.(selectedValues);
              }}
              options={options}
              label={fieldLabel}
              aria-invalid={hasError}
            />
            {hasError && <FieldError errors={[fieldState.error]} />}
            {description && <FieldDescription>{description}</FieldDescription>}
          </Field>
        );
      }}
    />
  );
};
